import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../../services/api';
import { ExternalLink, Clock } from 'lucide-react';

/**
 * NewsFeedPanel Component
 * Displays categorized news feed from chokepoints API
 */
const NewsFeedPanel = () => {
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const res = await dashboardAPI.getNews({ limit: 50 });
        setNews(res.data);
        setLastUpdate(new Date());
        setLoading(false);
      } catch (error) {
        console.error('Failed to load news', error);
        setLoading(false);
      }
    };

    loadNews();
    const interval = setInterval(loadNews, 30 * 60 * 1000); // Refresh every 30 minutes
    return () => clearInterval(interval);
  }, []);

  const categories = [
    { id: 'all', label: 'All News' },
    { id: 'oil', label: 'Oil' },
    { id: 'trade', label: 'Trade' },
    { id: 'aviation', label: 'Aviation' },
    { id: 'military', label: 'Military' },
  ];

  const getDisplayNews = () => {
    if (!news || !news.news) return [];
    if (activeCategory === 'all') {
      return [
        ...news.news.oil,
        ...news.news.trade,
        ...news.news.aviation,
        ...news.news.military,
      ].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
    }
    return news.news[activeCategory] || [];
  };

  const getImpactText = (item) => {
    const headline = (item.headline || '').toLowerCase();
    const summary = (item.ai_summary || '').toLowerCase();
    
    if (headline.includes('oil') || summary.includes('oil')) {
      return 'Impact: Oil prices may be affected';
    } else if (headline.includes('shipping') || summary.includes('shipping')) {
      return 'Impact: Shipping routes may be disrupted';
    } else if (headline.includes('flight') || summary.includes('aviation')) {
      return 'Impact: Air travel may be affected';
    } else if (headline.includes('trade') || summary.includes('trade')) {
      return 'Impact: Global trade flows may change';
    }
    return 'Impact: Monitoring situation';
  };

  return (
    <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          News Feed & Conflict Timeline
        </h2>
        {lastUpdate && (
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <Clock className="w-3 h-3 mr-1" />
            Updated {lastUpdate.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeCategory === cat.id
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            {cat.label}
            {news && news.news && news.news[cat.id] && (
              <span className="ml-2 text-xs">({news.news[cat.id].length})</span>
            )}
          </button>
        ))}
      </div>

      {/* News List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : getDisplayNews().length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No news available in this category.
        </div>
      ) : (
        <div className="space-y-4 max-h-[600px] overflow-y-auto">
          {getDisplayNews().map((item) => (
            <article
              key={item.id}
              className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                    {item.headline}
                  </h3>
                  <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span>{item.source}</span>
                    <span>•</span>
                    <time dateTime={item.published_at}>
                      {new Date(item.published_at).toLocaleString()}
                    </time>
                    {item.sentiment_score && (
                      <>
                        <span>•</span>
                        <span>Sentiment: {item.sentiment_score}/100</span>
                      </>
                    )}
                  </div>
                  {item.ai_summary && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {item.ai_summary}
                    </p>
                  )}
                  <p className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                    {getImpactText(item)}
                  </p>
                </div>
              </div>
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline mt-2"
                >
                  Read full article
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsFeedPanel;

